<template>
    <!-- 
    Dashboard Layout
    ---------------- 
    -->
    
    <!-- Full Width Topbar -->
    <header class="topbar">
        <div class="d-flex justify-content-between align-items-center h-100">
            <div class="topbar-left">
                <div class="topbar-brand">
                    <a href="#/home" class="logo-link d-flex align-items-center justify-content-center">
                        <img src="/images/favicon.png" alt="7th Level Technologies" class="logo-img">
                        <h4 class="text-white mb-0 ms-2 d-none d-md-block">Radar App</h4>
                    </a>

                    <button
                        class="hamburger hamburger--slider"
                        :class="{ 'is-active': sidebarVisible }"
                        type="button"
                        aria-label="Toggle sidebar"
                        @click="toggleSidebar()"
                    >
                        <span class="hamburger-box">
                            <span class="hamburger-inner"></span>
                        </span>
                    </button>
                </div>
            </div>
            
            <!-- Header Content 
            <div class="d-flex align-items-center flex-grow-1 mx-4">
                <router-view name="header"></router-view>
            </div>
            -->
            
            <!-- Time Clock Button & User Dropdown -->
            <div class="d-flex align-items-center gap-3">
                <time-clock-button class="d-none d-md-flex" @open-modal="showTimeClockModal = true" />
                <user-dropdown @open-clock-modal="showTimeClockModal = true" />
            </div>
        </div>
    </header>
    
    <!-- Main Layout Container -->
    <div class="layout-container">
        <!-- Sidebar -->
        <side-bar></side-bar>
        
        <!-- Main Content -->
        <div class="main-content" :class="{ 'sidebar-collapsed': !sidebarVisible }">

            <!-- Main Section -->
            <main class="main-section">
            <div class="main-wrap">
                <div class="container-fluid">
                    <router-view name="page"></router-view>
                </div>
                </div>
                <!-- Footer Section -->
                <div class="container-fluid px-0 mt-auto">
                    <div class="footer-section">
                        <p class="mb-0">
                            &copy; 2025 - 7th Level Technologies. 
                            All rights reserved.
                            <span class="ms-2 text-muted">v{{ appVersion }}</span>
                        </p>
                    </div>
                </div>
            </main>
        </div>
    </div>

    <!-- Route-based Modals (from router configuration) -->
    <router-view name="modal"></router-view>

    <!-- Global Modals - only show if not already on a route-based modal -->
    <job-edit v-if="showJobModal && !isOnJobRoute" :id="jobId" @close="showJobModal = false" />
    <assignment-edit 
      v-if="showAssignmentModal" 
      :id="taskId" 
      :request-id="assignmentRequestId"
      :client-id="assignmentClientId"
      :tech-id="assignmentTechId"
      :work-assignment="assignmentWorkAssignment"
      :is-new="!taskId"
      @close="showAssignmentModal = false" 
    />
    <time-edit v-if="showTimeModal" :id="timeId" @close="showTimeModal = false" />
    <work-edit v-if="showWorkModal" :id="workId" :request-id="workRequestId" :client-id="workClientId" :tech-id="workTechId" @close="closeWorkModal" @saved="closeWorkModal" />
    <client-edit v-if="showClientModal" :id="clientId" @cancel="closeClientModal" @saved="closeClientModal" />
    <tech-edit v-if="showTechModal" :id="techId" @cancel="closeTechModal" @saved="closeTechModal" />
    <part-edit v-if="showPartModal" :id="partId" :request-id="partRequestId" :client-id="partClientId" :tech-id="partTechId" @cancel="closePartModal" @saved="closePartModal" />
    <expense-edit v-if="showExpenseModal" :id="expenseId" :request-id="expenseRequestId" :client-id="expenseClientId" :tech-id="expenseTechId" @cancel="closeExpenseModal" @saved="closeExpenseModal" />
    <mileage-edit v-if="showMileageModal" :id="mileageId" :request-id="mileageRequestId" :client-id="mileageClientId" :tech-id="mileageTechId" @cancel="closeMileageModal" @saved="closeMileageModal" />
    <work-order-edit v-if="showWorkOrderModal" :id="workOrderId" @close="closeWorkOrderModal" />
    <estimate-edit-embedded 
      v-if="showEstimateModal" 
      :record-id="estimateRecordId" 
      :request-id="estimateRequestId" 
      :client-id="estimateClientId" 
      @close="closeEstimateModal" 
    />
    <request-edit v-if="showRequestModal" :id="requestId" @close="closeRequestModal" />
    <time-clock-modal v-if="showTimeClockModal" @close="showTimeClockModal = false" />
    
    <!-- Clock Wizard Modal -->
    <kt-modal v-if="showClockWizardModal" :visible="showClockWizardModal" title="Start Clock" @dismiss="closeClockWizardModal">
      <template #body>
        <clock-wizard 
          :operation="clockWizardOperation" 
          :request-id="clockWizardRequestId" 
          :task-id="clockWizardTaskId"
          @cancel="closeClockWizardModal" 
          @success="closeClockWizardModal" 
        />
      </template>
    </kt-modal>
    
</template>

<script setup>
    import { ref, computed } from "vue";
    import { RouterView, RouterLink, useRoute } from 'vue-router';
    import SideBar from "@/components/Shared/Sidebar.vue";
    
    const route = useRoute();
    import UserDropdown from "@/components/Shared/UserDropdown.vue";
    import TimeClockButton from "@/components/TimeClock/TimeClockButton.vue";
    import TimeClockModal from "@/components/TimeClock/TimeClockModal.vue";
    import JobEdit from "@/components/Jobs/JobEdit.vue";
    import TimeEdit from "@/components/Time/TimeEdit.vue";
    import AssignmentEdit from "@/components/Time/AssignmentEdit.vue";
    import WorkEdit from "@/components/Work/WorkEdit.vue";
    import ClientEdit from "@/components/Clients/ClientEdit.vue";
    import TechEdit from "@/components/Techs/TechEdit.vue";
    import PartEdit from "@/components/Parts/PartEdit.vue";
    import ExpenseEdit from "@/components/Expenses/ExpenseEdit.vue";
    import MileageEdit from "@/components/Mileage/MileageEdit.vue";
    import WorkOrderEdit from "@/components/WorkOrders/WorkOrderEdit.vue";
    import RequestEdit from "@/components/Requests/RequestEdit.vue";
    import EstimateEditEmbedded from "@/components/Estimates/EstimateEditEmbedded.vue";
    import ClockWizard from "@/components/Clock/ClockWizard.vue";
    import KtModal from "@/components/UI/KtModal.vue";
    import Emitter from "@/services/Emitter";
    import Api from "@/services/Api";
    import ApiService from "@/services/ApiService";
    
    
    import AppHeader from "@/components/Shared/AppHeader.vue";
    import AppFooter from "@/components/Shared/AppFooter.vue";
    import KtOffcanvas from "@/components/UI/KtOffcanvas.vue";
    import KtLoading from '@/components/UI/KtLoading.vue';
    import Events from "@/services/Emitter";
    import Helper from "@/services/Helper";
    
    const appVersion = __APP_VERSION__;
    
    const showSidebar = ref(false);
    const sidebarVisible = ref(true); // Track sidebar state
    const loading = ref(0);
    const version = ref(0);

    const showJobModal = ref(false);
    const jobId = ref(null); 

    // Assignment modal
    const showAssignmentModal = ref(false);
    const taskId = ref(null);
    const assignmentRequestId = ref(null);
    const assignmentClientId = ref(null);
    const assignmentTechId = ref(null);
    const assignmentWorkAssignment = ref('');

    const showClientModal = ref(false);
    const clientId = ref(null);
    
    const showTechModal = ref(false);
    const techId = ref(null);
    
    const showPartModal = ref(false);
    const partId = ref(null);
    const partRequestId = ref(null);
    const partClientId = ref(null);
    const partTechId = ref(null);
    
    const showExpenseModal = ref(false);
    const expenseId = ref(null);
    const expenseRequestId = ref(null);
    const expenseClientId = ref(null);
    const expenseTechId = ref(null);
    
    const showMileageModal = ref(false);
    const mileageId = ref(null);
    const mileageRequestId = ref(null);
    const mileageClientId = ref(null);
    const mileageTechId = ref(null);
    
    const showWorkOrderModal = ref(false);
    const workOrderId = ref(null);
    
    // Estimate modal
    const showEstimateModal = ref(false);
    const estimateRecordId = ref('new');
    const estimateRequestId = ref(null);
    const estimateClientId = ref(null);
    
    const showRequestModal = ref(false);
    const requestId = ref(null);
    
    const showTimeModal = ref(false);
    const timeId = ref(null);
    
    const showWorkModal = ref(false);
    const workId = ref(null);
    const workRequestId = ref(null);
    const workClientId = ref(null);
    const workTechId = ref(null);
    
    const showTimeClockModal = ref(false);
    
    // Clock Wizard modal
    const showClockWizardModal = ref(false);
    const clockWizardOperation = ref('start');
    const clockWizardRequestId = ref(null);
    const clockWizardTaskId = ref(null);
    
    // Check if we're on a route that already has a job modal
    const isOnJobRoute = computed(() => {
        return route.name && route.name.toString().includes('Job') && route.path.includes('/job/edit');
    });
    
    const refresh = () =>
    {
        window.location.reload(true);
    };
    
    const toggleSidebar = () =>
    {
      Emitter.emit("sidebar");
    }
    
    // Listen for sidebar state changes
    Emitter.on("sidebar-state", (visible) => {
      sidebarVisible.value = visible;
    });
    
    // Listen for global modal events
    Emitter.on("edit:job", (id) => {
      jobId.value = id || null;
      showJobModal.value = true;
    });

    Emitter.on("open:taskForm", (payload) => {
      // Support both simple id or object with additional props
      if (typeof payload === 'object' && payload !== null) {
        taskId.value = payload.id || null;
        assignmentRequestId.value = payload.requestId || null;
        assignmentClientId.value = payload.clientId || null;
        assignmentTechId.value = payload.techId || null;
        assignmentWorkAssignment.value = payload.workAssignment || '';
      } else {
        taskId.value = payload || null;
        assignmentRequestId.value = null;
        assignmentClientId.value = null;
        assignmentTechId.value = null;
        assignmentWorkAssignment.value = '';
      }
      showAssignmentModal.value = true;
    });

    Emitter.on("open:workForm", (id) => {
      timeId.value = id || null;
      showTimeModal.value = true;
    });

    // Work Entry modal (WorkEdit - simplified work form)
    Emitter.on("open:work", (payload) => {
      if (typeof payload === 'object' && payload !== null) {
        workId.value = payload.id || null;
        workRequestId.value = payload.requestId || null;
        workClientId.value = payload.clientId || null;
        workTechId.value = payload.techId || null;
      } else {
        workId.value = payload || null;
        workRequestId.value = null;
        workClientId.value = null;
        workTechId.value = null;
      }
      showWorkModal.value = true;
    });
    
    const closeWorkModal = () => {
      showWorkModal.value = false;
      workId.value = null;
      workRequestId.value = null;
      workClientId.value = null;
      workTechId.value = null;
    };

    // old format ...
    // Deprecated: use open:taskForm instead
    Emitter.on("open-assignment-modal", () => {
      showAssignmentModal.value = true;
    });
    
    // Alias for open:taskForm - used by ManagementReviewForm
    Emitter.on("open:assignmentForm", (payload) => {
      if (typeof payload === 'object' && payload !== null) {
        taskId.value = payload.id || null;
        assignmentRequestId.value = payload.requestId || null;
        assignmentClientId.value = payload.clientId || null;
        assignmentTechId.value = payload.techId || null;
        assignmentWorkAssignment.value = payload.workAssignment || '';
      } else {
        taskId.value = payload || null;
        assignmentRequestId.value = null;
        assignmentClientId.value = null;
        assignmentTechId.value = null;
        assignmentWorkAssignment.value = '';
      }
      showAssignmentModal.value = true;
    });
    
    // Client modal
    // Usage: Emitter.emit("open:clientForm", id) - pass id to edit, or no id for new
    Emitter.on("open:clientForm", (id) => {
      clientId.value = id || null;
      showClientModal.value = true;
    });
    
    const closeClientModal = () => {
      showClientModal.value = false;
      clientId.value = null;
    };
    
    // Tech modal  
    // Usage: Emitter.emit("open:techForm", id) - pass id to edit, or no id for new
    Emitter.on("open:techForm", (id) => {
      techId.value = id || null;
      showTechModal.value = true;
    });
    
    const closeTechModal = () => {
      showTechModal.value = false;
      techId.value = null;
    };
    
    // Part modal
    // Usage: Emitter.emit("open:partForm", id) or Emitter.emit("open:partForm", { requestId, clientId, techId })
    Emitter.on("open:partForm", (payload) => {
      if (typeof payload === 'object' && payload !== null) {
        partId.value = payload.id || null;
        partRequestId.value = payload.requestId || null;
        partClientId.value = payload.clientId || null;
        partTechId.value = payload.techId || null;
      } else {
        partId.value = payload || null;
        partRequestId.value = null;
        partClientId.value = null;
        partTechId.value = null;
      }
      showPartModal.value = true;
    });
    
    const closePartModal = () => {
      showPartModal.value = false;
      partId.value = null;
      partRequestId.value = null;
      partClientId.value = null;
      partTechId.value = null;
    };
    
    // Expense modal
    // Usage: Emitter.emit("open:expenseForm", id) or Emitter.emit("open:expenseForm", { requestId, clientId, techId })
    Emitter.on("open:expenseForm", (payload) => {
      if (typeof payload === 'object' && payload !== null) {
        expenseId.value = payload.id || null;
        expenseRequestId.value = payload.requestId || null;
        expenseClientId.value = payload.clientId || null;
        expenseTechId.value = payload.techId || null;
      } else {
        expenseId.value = payload || null;
        expenseRequestId.value = null;
        expenseClientId.value = null;
        expenseTechId.value = null;
      }
      showExpenseModal.value = true;
    });
    
    const closeExpenseModal = () => {
      showExpenseModal.value = false;
      expenseId.value = null;
      expenseRequestId.value = null;
      expenseClientId.value = null;
      expenseTechId.value = null;
    };
    
    // Mileage modal
    // Usage: Emitter.emit("open:mileageForm", id) or Emitter.emit("open:mileageForm", { requestId, clientId, techId })
    Emitter.on("open:mileageForm", (payload) => {
      if (typeof payload === 'object' && payload !== null) {
        mileageId.value = payload.id || null;
        mileageRequestId.value = payload.requestId || null;
        mileageClientId.value = payload.clientId || null;
        mileageTechId.value = payload.techId || null;
      } else {
        mileageId.value = payload || null;
        mileageRequestId.value = null;
        mileageClientId.value = null;
        mileageTechId.value = null;
      }
      showMileageModal.value = true;
    });
    
    const closeMileageModal = () => {
      showMileageModal.value = false;
      mileageId.value = null;
      mileageRequestId.value = null;
      mileageClientId.value = null;
      mileageTechId.value = null;
    };
    
    // WorkOrder modal
    // Usage: Emitter.emit("edit:workorder", id) - pass id to edit, or no id for new
    // Deprecated: use edit:workorder instead
    Emitter.on("open-workorder-modal", () => {
      workOrderId.value = null;
      showWorkOrderModal.value = true;
    });
    
    Emitter.on("edit:workorder", (id) => {
      workOrderId.value = id || null;
      showWorkOrderModal.value = true;
    });
    
    const closeWorkOrderModal = () => {
      showWorkOrderModal.value = false;
      workOrderId.value = null;
    };
    
    // Request modal (simplified work order entry)
    // Usage: Emitter.emit("open:requestForm", id) - pass id to edit, or no id for new
    Emitter.on("open:requestForm", (id) => {
      requestId.value = id || null;
      showRequestModal.value = true;
    });
    
    const closeRequestModal = () => {
      showRequestModal.value = false;
      requestId.value = null;
    };
    
    // Estimate modal
    // Usage: Emitter.emit("open:estimateForm", { requestId: 123, clientId: 456 }) or Emitter.emit("open:estimateForm", id)
    Emitter.on("open:estimateForm", (payload) => {
      if (typeof payload === 'object' && payload !== null) {
        estimateRecordId.value = payload.id ? String(payload.id) : 'new';
        estimateRequestId.value = payload.requestId || null;
        estimateClientId.value = payload.clientId || null;
      } else {
        estimateRecordId.value = payload ? String(payload) : 'new';
        estimateRequestId.value = null;
        estimateClientId.value = null;
      }
      showEstimateModal.value = true;
    });
    
    const closeEstimateModal = () => {
      showEstimateModal.value = false;
      estimateRecordId.value = 'new';
      estimateRequestId.value = null;
      estimateClientId.value = null;
    };
    
    // Clock Wizard modal
    // Usage: Emitter.emit("new:clock", { requestId: 123 }) or Emitter.emit("new:clock", { taskId: 456 })
    Emitter.on("new:clock", (payload) => {
      if (typeof payload === 'object' && payload !== null) {
        clockWizardRequestId.value = payload.requestId || null;
        clockWizardTaskId.value = payload.taskId || null;
        clockWizardOperation.value = payload.operation || 'start';
      } else {
        clockWizardRequestId.value = null;
        clockWizardTaskId.value = null;
        clockWizardOperation.value = 'start';
      }
      showClockWizardModal.value = true;
    });
    
    // Also handle new:assignment for the Add Assignment button in JobForm
    Emitter.on("new:assignment", (payload) => {
      if (typeof payload === 'object' && payload !== null) {
        taskId.value = null;
        assignmentRequestId.value = payload.requestId || null;
        assignmentClientId.value = payload.clientId || null;
        assignmentTechId.value = payload.techId || null;
        assignmentWorkAssignment.value = '';
      } else {
        taskId.value = null;
        assignmentRequestId.value = null;
        assignmentClientId.value = null;
        assignmentTechId.value = null;
        assignmentWorkAssignment.value = '';
      }
      showAssignmentModal.value = true;
    });
    
    const closeClockWizardModal = () => {
      showClockWizardModal.value = false;
      clockWizardRequestId.value = null;
      clockWizardTaskId.value = null;
      clockWizardOperation.value = 'start';
      requestId.value = null;
    };
    
</script>

<style scoped>
    .topbar {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        height: 70px;
        background-color: rgb(34,30,31);
        background-color: #132341;
        padding: 0 1.5rem;
        border-bottom: 1px solid #495057;
        z-index: 1001;
        color: #fff;
    }

    .topbar-left {
        width: 300px;
        min-width: 300px;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    @media (max-width: 768px) {
        .topbar-left {
            width: auto;
            min-width: auto;
        }

        /* On mobile: hamburger first, then logo */
        .topbar-brand {
            flex-direction: row-reverse;
        }
    }

    .topbar-brand {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
    }

    .topbar-left .hamburger {
        padding: 6px;
        transform: scale(0.75);
        transform-origin: center;
    }

    .topbar-left .hamburger,
    .topbar-left .hamburger:hover,
    .topbar-left .hamburger:focus {
        background: transparent;
        box-shadow: none;
        outline: none;
    }

    .topbar-left .hamburger-inner,
    .topbar-left .hamburger-inner::before,
    .topbar-left .hamburger-inner::after {
        background-color: #fff;
    }
    
    .logo-link {
        text-decoration: none;
    }
    
    .logo-img {
        width: 40px;
        height: auto;
    }
    
    .layout-container {
        margin-top: 70px;
        display: flex;
        height: calc(100vh - 70px);
    }
    
    .main-content {
        margin-left: 300px;
        flex: 1;
        display: flex;
        flex-direction: column;
        height: 100%;
        transition: margin-left 0.3s ease-in-out;
        overflow-y: auto;
    }
    
    .main-content.sidebar-collapsed {
        margin-left: 0;
    }
    .main-section {
        flex: 1;
        background-color: #ffffff;
        background-color: #f2f3f6;
        overflow-y:auto;
        display: flex;
        flex-direction: column;
    }

    .main-wrap {
        padding: 1.5rem;
    }

    .footer-section {
        padding: 1rem;
        background-color: #f8f9fa;
        border-top: 1px solid #dee2e6;
        text-align: center;
        color: #6c757d;
    }
    .search-bar {
        max-width: 400px;
    }


</style>
    